import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../componentes/modal/modal";
import Button from "../../../../componentes/button/button";
import Input from "../../../../componentes/input/input";
import InputFormat from "../../../../componentes/inputMask/inputMask";
import FornecedorService from "../../../../services/resource/forncedorService";

const fornecedorService = new FornecedorService();

const inicial = {
    juridico: true,
    nome: "",
    nomeFantasia: "",
    cnpj: "",
    cpf: "",
    // Preenchidos automaticamente pela busca de CNPJ — não exibidos neste modal rápido, mas
    // enviados junto no cadastro para já enriquecer o registro (ajustáveis depois em Fornecedores).
    email: "",
    telefone1: "",
    cep: "",
    endereco: "",
    numero: "",
    bairro: "",
    complemento: "",
};

/**
 * Cadastro rápido de fornecedor a partir da busca — só os campos exigidos pelo backend
 * (nome fantasia + CNPJ ou CPF conforme o tipo de pessoa). Dados complementares (endereço,
 * contato, tipo de fornecedor) ficam para a tela de gestão de fornecedores — mas se o CNPJ for
 * encontrado na Receita Federal, eles já são preenchidos silenciosamente ao sair do campo.
 */
export default function CadastrarFornecedorModal({ isOpen, onClose, onCriado }) {
    const [form, setForm] = useState(inicial);
    const [salvando, setSalvando] = useState(false);
    const [buscandoCnpj, setBuscandoCnpj] = useState(false);

    useEffect(() => {
        if (isOpen) setForm(inicial);
    }, [isOpen]);

    function set(campo, valor) {
        setForm(prev => ({ ...prev, [campo]: valor }));
    }

    function aoSairDoCnpj() {
        const cnpjLimpo = (form.cnpj || "").replace(/\D/g, "");
        if (cnpjLimpo.length !== 14) return;

        setBuscandoCnpj(true);
        fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjLimpo}`)
            .then(res => {
                if (!res.ok) throw new Error("CNPJ não encontrado.");
                return res.json();
            })
            .then(data => {
                setForm(prev => ({
                    ...prev,
                    nome: data.razao_social || prev.nome,
                    nomeFantasia: data.nome_fantasia || data.razao_social || prev.nomeFantasia,
                    email: data.email || prev.email,
                    telefone1: data.ddd_telefone_1 || prev.telefone1,
                    cep: data.cep ? String(data.cep) : prev.cep,
                    endereco: data.logradouro || prev.endereco,
                    numero: data.numero || prev.numero,
                    bairro: data.bairro || prev.bairro,
                    complemento: data.complemento || prev.complemento,
                }));
                toast.success("Dados do CNPJ preenchidos automaticamente.");
            })
            .catch(() => toast.info("CNPJ não encontrado. Preencha os dados manualmente."))
            .finally(() => setBuscandoCnpj(false));
    }

    function salvar() {
        if (!form.nome.trim() || !form.nomeFantasia.trim()) {
            toast.error("Informe a Razão Social e o Nome Fantasia.");
            return;
        }

        setSalvando(true);
        fornecedorService.salvar({
            nome: form.nome.trim(),
            nomeFantasia: form.nomeFantasia.trim(),
            juridico: form.juridico,
            cnpj: form.juridico ? form.cnpj : "",
            cpf: form.juridico ? "" : form.cpf,
            ativo: true,
            email: form.email,
            telefone1: form.telefone1,
            cep: form.cep,
            endereco: form.endereco,
            numero: form.numero,
            bairro: form.bairro,
            complemento: form.complemento,
        })
            .then(res => {
                toast.success("Fornecedor cadastrado com sucesso.");
                onCriado(res.data);
            })
            .catch(error => {
                const mensagem = error?.response?.data?.message || "Erro ao cadastrar fornecedor.";
                console.error(mensagem);
            })
            .finally(() => setSalvando(false));
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Cadastrar Fornecedor"
            subtitle="Cadastro rápido — dados complementares podem ser preenchidos depois em Fornecedores."
            className="w-[90%] max-w-xl"
            footer={
                <div className="flex justify-end gap-2">
                    <Button variant="outline" label="Cancelar" onClick={onClose} disabled={salvando} />
                    <Button variant="blue" label="Cadastrar" iconLeft="fa fa-check" loading={salvando} onClick={salvar} />
                </div>
            }
        >
            <div className="p-3 py-4 flex flex-col gap-4">
                <div className="flex gap-2">
                    <Button
                        variant={form.juridico ? "blue" : "outline"}
                        label="Pessoa Jurídica"
                        onClick={() => set("juridico", true)}
                    />
                    <Button
                        variant={!form.juridico ? "blue" : "outline"}
                        label="Pessoa Física"
                        onClick={() => set("juridico", false)}
                    />
                </div>
                {form.juridico ? (
                    <InputFormat
                        label="CNPJ"
                        required
                        mask="99.999.999/9999-99"
                        value={form.cnpj}
                        onChange={valor => set("cnpj", valor ?? "")}
                        onBlur={aoSairDoCnpj}
                        helpText={buscandoCnpj ? "Buscando dados do CNPJ..." : undefined}
                    />
                ) : (
                    <InputFormat label="CPF" required mask="999.999.999-99" value={form.cpf} onChange={valor => set("cpf", valor ?? "")} />
                )}
                <Input label="Razão Social" required value={form.nome} onChange={e => set("nome", e.target.value)} />
                <Input label="Nome Fantasia" required value={form.nomeFantasia} onChange={e => set("nomeFantasia", e.target.value)} />
            </div>
        </Modal>
    );
}
